import { FC, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Appointment, fetchAppointment, convertAppointmentToOrder } from '../api/appointments';

const labelCx =
  'text-[11px] font-semibold uppercase tracking-wide text-slate-400';

const AppointmentViewPage: FC = () => {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string })?.from;
  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [converting, setConverting] = useState(false);
  const [convertError, setConvertError] = useState<string | null>(null);
  const [showConvertModal, setShowConvertModal] = useState(false);

  useEffect(() => {
    if (!appointmentId) {
      setError('Missing appointment id.');
      setLoading(false);
      return;
    }
    let ignore = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchAppointment(appointmentId);
        if (!ignore) setAppointment(data);
      } catch (err) {
        if (!ignore) {
          setError(
            err instanceof Error ? err.message : 'Failed to load appointment',
          );
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    void load();
    return () => {
      ignore = true;
    };
  }, [appointmentId]);

  const customerName = useMemo(() => {
    if (!appointment?.customer) return 'Customer';
    const c = appointment.customer;
    const name = `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim();
    return (
      name || c.email || c.mobileNumber || c.mobileNumber || 'Unnamed customer'
    );
  }, [appointment]);

  const formatScheduledWithDelta = (iso: string) => {
    const scheduled = new Date(iso);
    const now = new Date();
    const msPerDay = 24 * 60 * 60 * 1000;
    const diff = Math.ceil(
      (scheduled.getTime() - now.getTime()) / msPerDay,
    );
    let suffix = '';
    if (diff > 0) {
      suffix = `(${diff} day${diff === 1 ? '' : 's'} to go)`;
    } else if (diff === 0) {
      suffix = '(today)';
    } else {
      const past = Math.abs(diff);
      suffix = `(${past} day${past === 1 ? '' : 's'} ago)`;
    }
    return `${scheduled.toLocaleString()} ${suffix}`;
  };

  if (loading) {
    return (
      <div className="p-6 text-sm text-slate-600">Loading appointment…</div>
    );
  }

  if (error || !appointment) {
    return (
      <div className="p-6 text-sm text-danger">
        {error || 'Appointment not found.'}
      </div>
    );
  }

  const vehicleLabel = appointment.vehicle
    ? [
      appointment.vehicle.vehicleBrand?.name,
      appointment.vehicle.vehicleModel?.name,
      appointment.vehicle.vehicleRegistrationNumber,
    ]
      .filter(Boolean)
      .join(' · ')
    : '—';

  const locationLabel =
    appointment.location?.name ??
    appointment.locationId ??
    '—';

  const statusLabel = appointment.status.replace(/_/g, ' ');

  return (
    <div className="mx-auto w-full max-w-[96rem] rounded-[28px] border border-slate-200/70 bg-white/95 p-10 shadow-[0_20px_45px_-32px_rgba(37,40,85,0.55)]">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-800">
            Appointment Details
          </h1>
          <p className="max-w-2xl text-sm text-slate-500">
            View full appointment information including customer, vehicle,
            schedule, and services.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
            onClick={() => navigate(from || '/appointments')}
          >
            {from ? 'Back to customer' : 'Back to list'}
          </button>
          <button
            type="button"
            className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
            onClick={() => navigate(`/appointments/${appointment.id}/edit`)}
          >
            Edit appointment
          </button>
          {appointment.orderId ? (
            <>
              <a
                href={`/order-management/${appointment.orderId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
              >
                View order
              </a>
              <button
                type="button"
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                onClick={() =>
                  navigate(
                    `/order-management/new/customer-vehicle?orderId=${appointment.orderId}`,
                  )
                }
              >
                Edit order
              </button>
            </>
          ) : (
            <button
              type="button"
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={converting}
              onClick={() => {
                setConvertError(null);
                setShowConvertModal(true);
              }}
            >
              {converting ? 'Converting…' : 'Convert to order'}
            </button>
          )}
        </div>
      </div>

      {convertError ? (
        <div className="mb-3 rounded-xl border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
          {convertError}
        </div>
      ) : null}

      <section className="space-y-4">
        <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
          <h2 className="text-sm font-semibold text-slate-700">
            Customer &amp; vehicle
          </h2>
          <dl className="mt-2 grid gap-3 text-sm text-slate-700 sm:grid-cols-2">
            <div>
              <dt className={labelCx}>Customer</dt>
              <dd className="mt-0.5 font-medium text-slate-800">
                {customerName}
              </dd>
            </div>
            <div>
              <dt className={labelCx}>Phone</dt>
              <dd className="mt-0.5">
                {appointment.customer?.mobileNumber ?? '—'}
              </dd>
            </div>
            <div>
              <dt className={labelCx}>Vehicle</dt>
              <dd className="mt-0.5">{vehicleLabel}</dd>
            </div>
            <div>
              <dt className={labelCx}>Location</dt>
              <dd className="mt-0.5">{locationLabel}</dd>
            </div>
          </dl>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
          <h2 className="text-sm font-semibold text-slate-700">
            Appointment details
          </h2>
          <dl className="mt-2 grid gap-3 text-sm text-slate-700 sm:grid-cols-2">
            <div>
              <dt className={labelCx}>Scheduled at</dt>
              <dd className="mt-0.5">
                {formatScheduledWithDelta(appointment.scheduledAt)}
              </dd>
            </div>
            <div>
              <dt className={labelCx}>Status</dt>
              <dd className="mt-0.5 capitalize">{statusLabel}</dd>
            </div>
            <div>
              <dt className={labelCx}>Source</dt>
              <dd className="mt-0.5">
                {appointment.source === 'phone' ? 'Phone' : 'Web link'}
              </dd>
            </div>
            <div>
              <dt className={labelCx}>Created</dt>
              <dd className="mt-0.5">
                {new Date(appointment.createdAt).toLocaleString()}
              </dd>
            </div>
          </dl>
          <div className="mt-3">
            <dt className={labelCx}>Notes</dt>
            <dd className="mt-1 rounded-xl bg-white/80 p-3 text-sm text-slate-700">
              {appointment.notes ? appointment.notes : 'No notes added.'}
            </dd>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <h2 className="text-sm font-semibold text-slate-700">Services</h2>
          {appointment.services && appointment.services.length > 0 ? (
            <ul className="mt-2 divide-y divide-slate-100 text-sm text-slate-700">
              {appointment.services.map((s) => (
                <li key={s.id} className="flex items-center justify-between py-2">
                  <div>
                    <div className="font-medium text-slate-800">
                      {s.name}
                    </div>
                    <div className="text-xs text-slate-500">
                      {s.variantCode || s.erpId}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-sm text-slate-500">
              No services linked to this appointment.
            </p>
          )}
        </div>
      </section>

      {showConvertModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
            <h2 className="text-base font-semibold text-slate-800">
              Convert appointment to order
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              This will create a new order linked to this appointment so you can continue the workflow in Order Management.
            </p>
            <p className="mt-2 text-xs text-slate-500">
              Customer and vehicle will be reused from this appointment. You can adjust services and other details on the order page.
            </p>

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={converting}
                onClick={() => setShowConvertModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-lg bg-primary px-5 py-2 text-xs font-semibold text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-primary/60"
                disabled={converting}
                onClick={async () => {
                  if (!appointment) return;
                  setConverting(true);
                  setConvertError(null);
                  try {
                    const res = await convertAppointmentToOrder(
                      appointment.id,
                    );
                    navigate(
                      `/order-management/new/customer-vehicle?orderId=${res.orderId}`,
                    );
                  } catch (err) {
                    setConvertError(
                      err instanceof Error
                        ? err.message
                        : 'Failed to convert appointment.',
                    );
                  } finally {
                    setConverting(false);
                    setShowConvertModal(false);
                  }
                }}
              >
                {converting ? 'Converting…' : 'Confirm & continue'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentViewPage;
